import React from 'react';
import ReactModal from 'react-modal';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { societyApi } from '@/features/societies/api/societyApi';
import { StripeEmbeddedOnboarding } from './StripeEmbeddedOnboarding';

interface StripeOnboardingModalProps {
  societyId: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modale qui héberge le composant embedded Stripe Connect.
 *
 * Utilise ReactModal directement (et non le wrapper `Components/Modal/Modal`)
 * parce que le composant embedded Stripe est dense et n'a pas de scroll
 * interne — on a besoin de :
 *  - cap viewport (96vh × 90vw) au lieu de `inset: auto` (taille contenu)
 *  - délégation du scroll au modal-body via `minHeight: 0` + `overflow-y: auto`
 *
 * Sur `onExit` du composant (close ou onboarding terminé), on resynchronise
 * l'état du compte côté API pour ne pas attendre le webhook account.updated,
 * puis on invalide le cache React Query de la société.
 */
export const StripeOnboardingModal: React.FC<StripeOnboardingModalProps> = ({
  societyId,
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();

  if (!isOpen) return null;

  const handleExit = async () => {
    try {
      await societyApi.syncStripeAccountStatus(societyId);
    } catch {
      // Le webhook prendra le relais — pas bloquant.
    }
    queryClient.invalidateQueries({ queryKey: ['society', societyId] });
    queryClient.invalidateQueries({ queryKey: ['societies'] });
    queryClient.invalidateQueries({ queryKey: ['society-configuration', 'effective', societyId] });
    onClose();
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      appElement={document.getElementById('root') as HTMLElement}
      style={{
        overlay: {
          zIndex: 100000,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        content: {
          position: 'relative',
          inset: 'auto',
          width: '90vw',
          maxWidth: '900px',
          maxHeight: '90vh',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-6 tw-py-4 tw-border-b tw-border-gray-200 tw-flex-shrink-0">
        <h5 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0">
          Finalisation de votre compte Stripe
        </h5>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors"
        >
          <X className="tw-w-5 tw-h-5" />
        </button>
      </div>

      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          padding: '1rem',
        }}
      >
        <StripeEmbeddedOnboarding societyId={societyId} onExit={handleExit} />
      </div>
    </ReactModal>
  );
};
